"use client";

import { forwardRef } from "react";
import { cn } from "~/lib/utils";

export interface ToggleProps
  extends Omit<React.HTMLAttributes<HTMLButtonElement>, "onChange"> {
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
  label?: string;
  description?: string;
}

const Toggle = forwardRef<HTMLButtonElement, ToggleProps>(
  (
    {
      className,
      checked = false,
      onChange,
      disabled = false,
      label,
      description,
      ...props
    },
    ref
  ) => {
    const handleClick = () => {
      if (!disabled && onChange) {
        onChange(!checked);
      }
    };

    return (
      <div className={cn("flex flex-col gap-1", className)}>
        {label && (
          <div className="flex flex-col gap-1">
            <p className="text-sm font-semibold leading-5 text-[#1c2024]">
              {label}
            </p>
            {description && (
              <p className="text-sm leading-5 text-[rgba(0,0,0,0.7)]">
                {description}
              </p>
            )}
          </div>
        )}
        <button
          ref={ref}
          type="button"
          role="switch"
          aria-checked={checked}
          disabled={disabled}
          onClick={handleClick}
          className={cn(
            "relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#1c3144] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            checked ? "bg-[#1c3144]" : "bg-[rgba(0,6,46,0.2)]",
            !disabled && "cursor-pointer"
          )}
          {...props}
        >
          <span
            className={cn(
              "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
              checked ? "translate-x-4" : "translate-x-0.5"
            )}
          />
        </button>
      </div>
    );
  }
);

Toggle.displayName = "Toggle";

export { Toggle };

